import { faFileExport, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { ReactNode, useEffect, useMemo, useState, useRef } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { apiV2 } from '../../../api/ApiV2';
import Button from '../../../components/base/Button';
import Loader from '../../../components/common/Loader/Loader';
import Stepper from '../../../components/common/Stepper/Stepper';
import IMerchProductOrder from '../../../models/restful/IMerchProductOrder';
import IMerchStore from '../../../models/restful/IMerchStore';
import PATHS from '../../../paths';
import { useUser } from '../hooks/useUser';
import { pay } from '../orders-table/OrdersFunctions';
import Confirm from './Confirm/Confirm';
import Designs from './Designs/Designs';
import './OrderForm.scss';
import {
  confirmValid,
  designsValid,
  productsValid,
  shipmentValid
} from './OrderFormFunctions';
import Products from './Products/Products';
import Shipping from './Shipping/Shipping';
import classNames from 'classnames';

interface IOrderFormProps {}

type TStep = 'products' | 'design' | 'shipping' | 'confirm' | 'view';

const OrderForm: React.FunctionComponent<IOrderFormProps> = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getUser } = useUser();
  const user = getUser();

  const [step, setStep] = useState<TStep>('products');
  const [showRequired, setShowRequired] = useState<number>(0);
  const initialStepSet = useRef(false);

  // Fetch order data
  const {
    data: order = {} as IMerchProductOrder,
    isLoading: isLoadingOrder,
    refetch: refetchOrder
  } = useQuery({
    queryKey: ['orders', name],
    queryFn: async () => {
      const response = await apiV2.crud.read({
        dataObjectName: 'MerchProductOrder',
        id: `${name}`,
        field: 'Name',
        dataObjectParams: {
          _view_nested: true
        }
      });
      return response.item as IMerchProductOrder;
    },
    enabled: !!name
  });

  // Set initial step only once when order is first loaded
  useEffect(() => {
    if (order && order.ID && !initialStepSet.current) {
      if (productsValid(order)) setStep('design');
      if (designsValid(order)) setStep('shipping');
      if (shipmentValid(order)) setStep('confirm');
      initialStepSet.current = true;
    }
  }, [order]);

  // Fetch stores
  const { data: stores = [], isLoading: isLoadingStores } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await apiV2.crud.readCollection({
        dataObjectName: 'MerchStore',
        collectionParams: { perPage: 1000, pageNo: -1 }
      });
      return response.itemsCollection?.items as IMerchStore[];
    }
  });

  const isLoading = isLoadingOrder || isLoadingStores;

  const [StepsNavigation, setProgressStep, progressStep] = useMemo(() => {
    const getProgressStep = () => {
      if (step === 'products') return 1;
      if (step === 'design') return 2;
      if (step === 'shipping') return 3;
      if (step === 'confirm') return 4;
      return 1;
    };
    const progressStep = getProgressStep();

    const setStepByProgress = (idx: number) => {
      let st = '';
      if (idx === 1) {
        st = 'products';
      }
      if (idx === 2) {
        if (productsValid(order)) {
          if (showRequired > 0) setShowRequired(0);
          st = 'design';
        } else setShowRequired(1);
      }
      if (idx === 3) {
        if (designsValid(order)) {
          if (showRequired > 0) setShowRequired(0);
          st = 'shipping';
        } else setShowRequired(2);
      }
      if (idx === 4) {
        if (shipmentValid(order)) {
          if (showRequired > 0) setShowRequired(0);
          st = 'confirm';
        } else setShowRequired(3);
      }
      if (st) setStep(st as TStep);
    };

    const steps = !order?.ID
      ? []
      : [
          {
            title: 'Products',
            hasError: progressStep === 1 && !productsValid(order),
            onClick: () => setStepByProgress(1)
          },
          {
            title: 'Designs',
            hasError: progressStep === 2 && !designsValid(order),
            onClick: () => setStepByProgress(2)
          },
          {
            title: 'Shipping',
            hasError: progressStep === 3 && !shipmentValid(order),
            onClick: () => setStepByProgress(3)
          },
          {
            title: 'Confirm',
            hasError: progressStep === 4 && !confirmValid(order),
            onClick: () => setStepByProgress(4)
          }
        ];

    const StepsNavigation = () => (
      <div className="row justify-content-center flex-nowrap align-items-top">
        <div className="col-1 py-2">
          <div
            className={classNames('stepper__nav-button', {
              'stepper__nav-button--disabled': progressStep <= 1,
              'stepper__nav-button--active': progressStep > 1
            })}
            onClick={() =>
              progressStep > 1 ? setProgressStep(progressStep - 1) : null
            }
            role="button"
            tabIndex={0}
          >
            <span className="stepper__nav-arrow">←</span>
          </div>
        </div>
        <div className="col-8">
          <Stepper progress={progressStep - 1} steps={steps} />
        </div>
        <div className="col-1 py-2">
          {progressStep < 4 && (
            <div
              className={classNames('stepper__nav-button', {
                'stepper__nav-button--active': progressStep < 4
              })}
              onClick={() =>
                progressStep < 4 ? setProgressStep(progressStep + 1) : null
              }
              role="button"
              tabIndex={0}
            >
              <span className="stepper__nav-arrow">→</span>
            </div>
          )}
          {progressStep === 4 && (
            <button
              className="btn btn-success btn-sm me-3"
              onClick={async () => {
                await apiV2.order.confirmOrders({
                  ids: [Number(order.ID)]
                });
                !user.IsPostPaid
                  ? pay([Number(order.ID)])
                  : navigate(generatePath(PATHS.ORDERS));
              }}
            >
              {!user.IsPostPaid ? 'Confirm and pay' : 'Confirm'}
            </button>
          )}
        </div>
      </div>
    );

    return [StepsNavigation, setStepByProgress, progressStep] as [
      () => ReactNode,
      (idx: number) => void,
      number
    ];
  }, [step, order, showRequired]);

  if (isLoading) {
    return <Loader />;
  }

  if (!order?.ID) {
    return null;
  }

  return (
    <div className="order-form-cont">
      <div className="row">
        <div className="col-6">
          <h2 className="mb-4">Order {order.Name}</h2>
        </div>
        <div className="col-6">
          <div className="ms-xxl-auto text-end">
            <Button
              variant="link"
              className="text-900 me-4 px-0"
              onClick={() => refetchOrder()}
            >
              <FontAwesomeIcon icon={faRefresh} className="fs-9 me-2" />
              Reload
            </Button>
            <Button
              variant="link"
              className="text-900 me-4 px-0"
              onClick={() => navigate(generatePath(PATHS.ORDERS))}
            >
              <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
              Save and close
            </Button>
            <Button
              variant="link"
              className="text-900 me-4 px-0"
              onClick={() =>
                progressStep < 4 ? setProgressStep(progressStep + 1) : null
              }
            >
              <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
              Save
            </Button>
          </div>
        </div>
      </div>

      {StepsNavigation()}
      {step === 'products' && (
        <Products
          order={order}
          setOrder={updatedOrder => {
            queryClient.setQueryData(['orders', name], updatedOrder);
          }}
          showRequired={showRequired === 1}
        />
      )}
      {step === 'design' && (
        <Designs
          order={order}
          stores={stores}
          showRequired={showRequired === 2}
          updateOrderFromServer={refetchOrder}
        />
      )}
      {step === 'shipping' && (
        <Shipping
          order={order}
          shoi={order.ShipmentOrderItem}
          showRequired={showRequired === 3}
          updateOrderFromServer={refetchOrder}
        />
      )}
      {step === 'confirm' && <Confirm order={order} />}
      {StepsNavigation()}
    </div>
  );
};

export default OrderForm;
